import Button from "@/components/button/Button"
import { useAuth } from "@/components/context/AuthProvider"
import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import CreateApplicationButton from "@/components/create-application-button/CreateApplicationButton"
import { SvgIcon } from "@/components/icon"
import { globalFetchPolicy } from "@/components/layout/fetchPolicies"
import { ApplyItemType } from "@/components/modal-dark/modal-dark-platform-content/single-submission/SingleSubmission"
import HexagonGroup from "@/components/modal-dark/modal-dark-platform-content/start-notice/hexagon-group/HexagonGroup"
import Typography from "@/components/typography/Typography"
import { useGetUserStartNoticeQuery, useUpdateUserProfileMutation } from "@/graphql"
import { Checkbox, Col, Row, message } from "antd"
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint"
import { CheckboxChangeEvent } from "antd/es/checkbox"
import { FC, useEffect, useState } from "react"
import { useNavigate } from "react-router"
import "./StartNotice.less"
import applyImage03 from "./assets/arrows-circle.png"
import applyImage01 from "./assets/pc-file.png"
import applyImage02 from "./assets/simplification.png"

const { Title, Paragraph, Text } = Typography
const applyItems: ApplyItemType[] = [
  {
    id: "1",
    description: "Create & submit your application in less than a minute",
    imageUrl: applyImage01,
  },
  {
    id: "2",
    description: "We instantly route it to the correct department",
    imageUrl: applyImage02,
  },
  {
    id: "3",
    description: "Get your response and approval faster than ever",
    imageUrl: applyImage03,
  },
]

const applyAoiItems: ApplyItemType[] = [
  {
    id: "1",
    description: "Create & submit your AOI application in less than a minute",
    imageUrl: applyImage01,
  },
  {
    id: "2",
    description: "We review your AOI application against the assessment criteria.",
    imageUrl: applyImage02,
  },
  {
    id: "3",
    description: "You will receive a response within a few days.",
    imageUrl: applyImage03,
  },
]

const StartNotice: FC = () => {
  const { xxl, xl, lg, md, sm } = useBreakpoint()
  const { setModalDarkTypes, setIsModalDarkOpen } = useModalDarkContext()
  const handleSupportContact = () => {
    setModalDarkTypes("quick-contact")
  }
  const { user, organization } = useAuth()
  const navigate = useNavigate()
  const userPlatformType = user?.userProfile?.data?.attributes?.platformType
  const isAoiPlatform = userPlatformType === "aoi"

  const itemsToRender = isAoiPlatform ? applyAoiItems : applyItems

  const userId = user?.userProfile?.data?.id
  const [updateIsStartNoticeShown, { loading }] = useUpdateUserProfileMutation()

  const { data, loading: dataLoading } = useGetUserStartNoticeQuery({
    fetchPolicy: globalFetchPolicy,
    variables: { id: user?.userProfile?.data?.id || "" },
    skip: !user?.userProfile?.data?.id,
  })

  const backendValue = data?.userProfile?.data?.attributes?.isStartNoticeShown

  const [isChecked, setIsChecked] = useState<boolean | null>(null)

  const onCreateAppBtnClick = () => {
    setIsModalDarkOpen(false)
  }

  const onCreateAoiClick = () => {
    navigate(`/${organization}/dashboard/create-aoi/details`)
    setIsModalDarkOpen(false)
  }

  const onCheckboxChange = async (e: CheckboxChangeEvent) => {
    const checked = e.target.checked
    setIsChecked(checked)
    if (!userId) return
    try {
      await updateIsStartNoticeShown({
        variables: {
          id: userId,
          data: { isStartNoticeShown: checked },
        },
      })
    } catch (error: any) {
      setIsChecked(!checked) // rollback
      message.error(error.message)
    }
  }

  useEffect(() => {
    if (backendValue !== undefined && backendValue !== null) {
      setIsChecked(backendValue)
    }
  }, [backendValue])

  const handleClose = () => {
    setIsModalDarkOpen(false)
  }

  return (
    <Row justify={"center"} className={"start-notice-wrapper"}>
      <Col span={20} xxl={20} xl={20} lg={20} md={24} sm={24} xs={24}>
        <Title level={5} centered color={"white"} weight={"w300"}>
          {"Welcome and thank you for using our portal"}
        </Title>

        <Title
          level={!(xxl || xl || lg) ? 4 : 2}
          centered
          color={"white"}
          style={{ marginTop: 0, marginBottom: !(xxl || xl || lg) ? 24 : 0 }}
        >
          {isAoiPlatform ? (
            <>
              <span className={"bold"}>Area of Interest Application (AOI)</span>
              <span className={"w300"}> Steps</span>
            </>
          ) : (
            <>
              <span className={"w300"}>How It </span>
              <span className={"bold"}>Works</span>
            </>
          )}
        </Title>
      </Col>

      <Row justify={"center"} style={{ paddingBottom: isAoiPlatform ? 24 : 0 }}>
        <div className={"info-steps-wrapper"}>
          <div className={"how-to-start-wrapper"}>
            {itemsToRender?.map(it => (
              <div className={"step-wrapper"} key={it?.id}>
                <HexagonGroup id={it?.id} imageUrl={it?.imageUrl} />
                <div className={"text-wrapper"}>
                  <Paragraph centered color={"white"}>
                    {it?.description}
                  </Paragraph>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Row>

      {!isAoiPlatform && (
        <Row justify={"center"} style={{ padding: "24px 0" }} className={"help-paragraph"}>
          <Col span={14} xxl={14} xl={14} lg={16} md={16} sm={20} xs={23}>
            <Paragraph color={"website-secondary"} size={"xs"} centered>
              <span>{"For more details, check out our "}</span>
              <span className={"bold"}>{"User Guide"}</span>
              <span>{" and short video tutorials that walk you through the full application and approval flow."}</span>
            </Paragraph>
          </Col>
        </Row>
      )}

      <Col span={16} xxl={16} xl={16} lg={18} md={16} sm={20} xs={24} style={{ paddingBottom: 40 }}>
        <Row justify={"center"}>
          <Button btnType={"text"} className={"start-notice-support-btn"} onClick={handleSupportContact}>
            <div className={"support-wrapper"}>
              <div className={"hexagon-wrapper"}>
                <SvgIcon type={"headphones"} />
              </div>
              <Paragraph color={"website-secondary"}>
                {"Need help with the process? Contact our support team here"}
              </Paragraph>
            </div>
          </Button>
        </Row>
      </Col>

      <Col span={24}>
        <Row align={"middle"} justify={!(xxl || xl || lg) ? "center" : "space-between"}>
          {(xxl || xl || lg) && (
            <Col span={6} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {!dataLoading && <Checkbox checked={isChecked ?? false} onChange={onCheckboxChange} />}
              <Text color={"website-secondary"} size={"xs"}>
                {"Show on every start"}
              </Text>
            </Col>
          )}
          <Col span={12} xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
            <Row justify={"center"} gutter={[16, 16]}>
              <Col span={12} xxl={12} xl={12} lg={12} md={12} sm={12} xs={24}>
                <Button
                  btnType={"ghost"}
                  text={"Close"}
                  textColor={"white"}
                  size={"large"}
                  onClick={handleClose}
                  block
                />
              </Col>

              <Col span={12} xxl={12} xl={12} lg={12} md={12} sm={12} xs={24}>
                {isAoiPlatform ? (
                  <Button
                    btnType={"primary"}
                    text={"Create AOI Application"}
                    onClick={onCreateAoiClick}
                    loading={loading}
                    size={"large"}
                  />
                ) : (
                  <CreateApplicationButton
                    block
                    size={"large"}
                    onClick={onCreateAppBtnClick}
                    loading={loading || dataLoading}
                  />
                )}
              </Col>

              {!(xxl || xl || lg) && (
                <Col span={24} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  {!dataLoading && <Checkbox checked={isChecked ?? false} onChange={onCheckboxChange} />}
                  <Text color={"website-secondary"} size={"xs"}>
                    {"Show on every start"}
                  </Text>
                </Col>
              )}
            </Row>
          </Col>
          <Col span={6} />
        </Row>
      </Col>
    </Row>
  )
}

export default StartNotice
